import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import type { AppError } from '../../core/error/appError';
import type { AppResult } from '../../core/result/appResult';
import { PhoneNormalizer } from '../../core/util/phoneNormalizer';
import { S } from '../../i18n/strings';
import {
  DoorApi,
  storedStaffToken,
  type AddedParty,
  type DoorBoard,
  type WaitEstimate,
  type WaitlistParty,
} from '../../data/door/doorApi';
import { ErrorBanner } from '../components/StateContainer';
import { CheckIcon, PhoneIcon, UsersIcon } from '../components/icons';

const RESTAURANT_ID = 'r-001';
/** La fila cambia sola: otro anfitrión puede anotar desde otra tableta. */
const REFRESH_MS = 15_000;

/**
 * Estación de puerta.
 *
 * Pantalla de personal, no de comensales: se opera de pie, con una mano, mientras alguien
 * espera enfrente. Por eso el formulario está siempre en el mismo sitio, las personas se eligen
 * tocando en vez de escribiendo, y la fila enseña de un vistazo lo único que no se puede
 * deducir mirando a la gente: a quién puede avisar el sistema y a quién hay que llamar.
 */
export function DoorScreen() {
  const [token, setToken] = useState<string | null>(() => storedStaffToken());
  if (!token) return <StaffGate onEnter={setToken} />;
  return <DoorBoardView token={token} onExpired={() => setToken(null)} />;
}

// ------------------------------------------------------------------ acceso

function StaffGate({ onEnter }: { onEnter: (token: string) => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<AppError | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const result = await DoorApi.login(pin);
    setBusy(false);
    if (result.ok) onEnter(result.data);
    else setError(result.error);
  };

  return (
    <form className="auth-card" onSubmit={submit}>
      <h1>Estación de puerta</h1>
      <p className="muted">
        Pantalla del personal. La fila lleva nombres y teléfonos de clientes, así que pide PIN.
      </p>
      {error && <ErrorBanner error={error} />}
      <div className="field">
        <label className="field__label" htmlFor="pin">
          PIN
        </label>
        <input
          id="pin"
          className="field__control door-pin"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(event) => setPin(event.target.value)}
        />
      </div>
      <button type="submit" className="btn btn--block" disabled={busy || pin.length === 0}>
        {busy ? S.loading : 'Entrar'}
      </button>
      <p className="muted">El servidor lo imprime al arrancar si no fijaste `STAFF_PIN`.</p>
    </form>
  );
}

// ------------------------------------------------------------------ panel

function DoorBoardView({ token, onExpired }: { token: string; onExpired: () => void }) {
  const [board, setBoard] = useState<DoorBoard | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const [added, setAdded] = useState<AddedParty | null>(null);

  const load = useCallback(async () => {
    const result = await DoorApi.board(RESTAURANT_ID, token);
    if (result.ok) {
      setBoard(result.data);
      setError(null);
      return;
    }
    // Sesión caducada o PIN revocado: se vuelve a pedir en lugar de dejar la pantalla muerta.
    if (result.error.kind === 'SESSION_EXPIRED') {
      DoorApi.logout();
      onExpired();
      return;
    }
    setError(result.error);
  }, [token, onExpired]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Se separan porque son dos tareas distintas: a los avisados hay que sentarlos, a los que
  // esperan hay que llamarlos. Mezclados, además, los números de lugar salían desordenados.
  const called = useMemo(() => board?.parties.filter((p) => p.status === 'CALLED') ?? [], [board]);
  const waiting = useMemo(() => board?.parties.filter((p) => p.status === 'WAITING') ?? [], [board]);
  const active = useMemo(() => [...called, ...waiting], [called, waiting]);
  const done = useMemo(
    () => board?.parties.filter((p) => p.status === 'SEATED' || p.status === 'LEFT') ?? [],
    [board],
  );

  /** Ejecuta una acción de la fila y recarga; el error se muestra sin perder lo que ya se veía. */
  const act = async <T,>(fn: () => Promise<AppResult<T>>) => {
    const result = await fn();
    if (!result.ok) setError(result.error);
    await load();
  };

  return (
    <div className="door">
      <header className="door__top">
        <div>
          <h1>Puerta</h1>
          <p className="muted">
            {board?.restaurant.name ?? '…'} · {board?.layout.tables.reduce((n, t) => n + t.count, 0) ?? 0} mesas ·
            turno medio {board?.layout.turnMinutes ?? 0} min
          </p>
        </div>
        <button
          type="button"
          className="btn btn--text"
          onClick={() => {
            DoorApi.logout();
            onExpired();
          }}
        >
          Salir
        </button>
      </header>

      {error && <ErrorBanner error={error} onRetry={load} />}

      <div className="door__pulse">
        <Pulse label="En espera" value={String(waiting.length)} unit="grupos"
          note={`${active.reduce((n, p) => n + p.party_size, 0)} personas en total`} />
        <Pulse label="Avisados" value={String(called.length)} unit="esperan pasar"
          note="ya se les dijo que su mesa está lista" />
        <Pulse label="Sentados hoy" value={String(done.filter((p) => p.status === 'SEATED').length)} unit="grupos"
          note={`${done.filter((p) => p.status === 'LEFT').length} se fueron sin esperar`} />
      </div>

      <div className="door__work">
        <AddPartyForm
          token={token}
          zones={board?.layout.zones ?? []}
          onAdded={(entry) => {
            setAdded(entry);
            load();
          }}
        />

        <div className="stack">
          {called.length > 0 && (
            <section className="section">
              <div className="section__header">
                <h2>Su mesa está lista</h2>
                <span className="muted">Ya avisados · falta sentarlos</span>
              </div>
              <div className="stack stack--tight">
                {called.map((party) => (
                  <PartyRow
                    key={party.id}
                    party={party}
                    onCall={(table) => act(() => DoorApi.call(RESTAURANT_ID, party.id, table, token))}
                    onSeat={(table) => act(() => DoorApi.seat(RESTAURANT_ID, party.id, table, token))}
                    onLeave={() => act(() => DoorApi.leave(RESTAURANT_ID, party.id, token))}
                  />
                ))}
              </div>
            </section>
          )}

          <div className="section__header">
            <h2>Fila de espera</h2>
            <span className="muted">Se actualiza sola cada 15 s</span>
          </div>

          {waiting.length === 0 ? (
            <p className="muted door__empty">Nadie esperando. La fila aparece aquí conforme anotes.</p>
          ) : (
            <div className="stack stack--tight">
              {waiting.map((party) => (
                <PartyRow
                  key={party.id}
                  party={party}
                  onCall={(table) => act(() => DoorApi.call(RESTAURANT_ID, party.id, table, token))}
                  onSeat={(table) => act(() => DoorApi.seat(RESTAURANT_ID, party.id, table, token))}
                  onLeave={() => act(() => DoorApi.leave(RESTAURANT_ID, party.id, token))}
                />
              ))}
            </div>
          )}

          {added && <JoinPanel entry={added} onClose={() => setAdded(null)} />}

          {done.length > 0 && (
            <section className="section" style={{ marginTop: 12 }}>
              <div className="section__header">
                <h2>Ya resueltos</h2>
                <span className="muted">Lo de hoy, para comparar espera real contra estimada</span>
              </div>
              <div className="stack stack--tight">
                {done.map((party) => (
                  <ResolvedRow key={party.id} party={party} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Pulse({ label, value, unit, note }: { label: string; value: string; unit: string; note: string }) {
  return (
    <div className="door__cell">
      <span className="door__label">{label}</span>
      <span className="door__value">
        {value} <small>{unit}</small>
      </span>
      <span className="muted">{note}</span>
    </div>
  );
}

// ------------------------------------------------------------------ anotar

const SIZES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function AddPartyForm({
  token,
  zones,
  onAdded,
}: {
  token: string;
  zones: readonly string[];
  onAdded: (entry: AddedParty) => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [size, setSize] = useState(2);
  const [zone, setZone] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [estimate, setEstimate] = useState<WaitEstimate | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const [busy, setBusy] = useState(false);

  // La estimación se pide al cambiar el tamaño: es lo que el anfitrión dice en voz alta antes
  // de anotar, así que tiene que estar en pantalla antes de pulsar el botón.
  useEffect(() => {
    let active = true;
    DoorApi.estimate(RESTAURANT_ID, size, token).then((result) => {
      if (active && result.ok) setEstimate(result.data);
    });
    return () => {
      active = false;
    };
  }, [size, token]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const result = await DoorApi.add(
      RESTAURANT_ID,
      {
        name,
        phone: phone.trim() || undefined,
        party_size: size,
        zone,
        notes: notes.trim() || null,
      },
      token,
    );
    setBusy(false);
    if (!result.ok) return setError(result.error);

    onAdded(result.data);
    setName('');
    setPhone('');
    setNotes('');
    setSize(2);
    setZone(null);
  };

  return (
    <form className="card door__form" onSubmit={submit}>
      <h2>Anotar en la lista</h2>
      {error && <ErrorBanner error={error} />}

      <div className="field">
        <label className="field__label" htmlFor="door-name">
          Nombre
        </label>
        <input id="door-name" className="field__control" value={name} onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="door-phone">
          Teléfono <span className="muted">— para llamarle si no vuelve</span>
        </label>
        <input
          id="door-phone"
          className="field__control"
          type="tel"
          inputMode="tel"
          placeholder="55 1234 5678"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>

      <div className="field">
        <span className="field__label" id="door-size">
          Personas
        </span>
        <div className="door__sizes" role="group" aria-labelledby="door-size">
          {SIZES.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={size === n}
              onClick={() => setSize(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {zones.length > 0 && (
        <div className="field">
          <span className="field__label" id="door-zone">
            Zona <span className="muted">— opcional</span>
          </span>
          <div className="row" role="group" aria-labelledby="door-zone">
            <button type="button" className="chip" aria-pressed={zone === null} onClick={() => setZone(null)}>
              Cualquiera
            </button>
            {zones.map((z) => (
              <button key={z} type="button" className="chip" aria-pressed={zone === z} onClick={() => setZone(z)}>
                {z}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="field">
        <label className="field__label" htmlFor="door-notes">
          Notas <span className="muted">— opcional</span>
        </label>
        <input id="door-notes" className="field__control" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      {estimate && (
        <div className="door__estimate">
          <span className="field__label">Le vas a decir</span>
          <span className="door__estimate-range">{estimate.text}</span>
          <span className="muted">
            {estimate.ahead === 0
              ? `Nadie por delante para ${size} ${size === 1 ? 'persona' : 'personas'}`
              : `${estimate.ahead} ${estimate.ahead === 1 ? 'grupo compite' : 'grupos compiten'} por las mismas mesas`}
            {estimate.tablesThatFit > 0 && ` · ${estimate.tablesThatFit} mesas donde cabe`}
          </span>
        </div>
      )}

      <button type="submit" className="btn btn--block" disabled={busy || name.trim().length < 2}>
        {busy ? S.loading : 'Anotar y mostrar QR'}
      </button>
    </form>
  );
}

// ------------------------------------------------------------------ el QR

function JoinPanel({ entry, onClose }: { entry: AddedParty; onClose: () => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!entry.join_url || !canvas.current) return;
    QRCode.toCanvas(canvas.current, entry.join_url, {
      width: 168,
      margin: 1,
      color: { dark: '#17110e', light: '#ffffff' },
    }).catch(() => {
      // Un QR que no se dibuja no puede quedar como un cuadro en blanco sin explicación: el
      // texto de al lado ya cuenta la alternativa por teléfono.
    });
  }, [entry.join_url]);

  return (
    <div className="door__join">
      {entry.join_url ? (
        <canvas ref={canvas} aria-label={`Código QR para que ${entry.name} vincule Telegram`} />
      ) : (
        <div className="door__join-noqr">Sin bot configurado</div>
      )}
      <div className="stack stack--tight">
        <h3>
          {entry.name} quedó anotada · {entry.position}º en la fila
        </h3>
        {entry.join_url ? (
          <>
            <p>
              Muéstrale la pantalla. Al escanear se abre el bot y su lugar queda vinculado: desde ahí le llega el
              aviso cuando su mesa esté lista, sin que tenga que quedarse en la puerta.
            </p>
            <p className="door__note">
              <strong>Si no escanea</strong>, su fila queda como <strong>Solo llamada</strong> y la avisas tú. El
              teléfono que anotaste sirve para eso: por sí solo no permite mandarle nada automático.
            </p>
          </>
        ) : (
          <p className="door__note door__note--warn">
            Este servidor no tiene bot de Telegram, así que no hay aviso automático. Anota el teléfono y llama.
          </p>
        )}
        <button type="button" className="btn btn--outline" onClick={onClose}>
          Listo
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ filas

function minutesSince(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
}

function PartyRow({
  party,
  onCall,
  onSeat,
  onLeave,
}: {
  party: WaitlistParty;
  onCall: (table: string | null) => void;
  onSeat: (table: string | null) => void;
  onLeave: () => void;
}) {
  const waited = minutesSince(party.created_at);
  const overEstimate = party.estimate_max !== null && waited > party.estimate_max;

  return (
    <article className={`door__row door__row--${party.status.toLowerCase()}${overEstimate ? ' door__row--over' : ''}`}>
      <span className="door__pos">{party.position ?? '·'}</span>

      <div className="stack stack--tight" style={{ minWidth: 0 }}>
        <div className="row">
          <strong>{party.name}</strong>
          <span className="door__size">
            <UsersIcon size={13} /> {party.party_size}
          </span>
          {party.status === 'CALLED' && <span className="status-chip status-chip--called">Avisado</span>}
        </div>
        <div className="meta-row">
          {/* La distinción que decide qué tiene que hacer el anfitrión. */}
          {party.reachable === 'TELEGRAM' ? (
            <span className="chip chip--static chip--open">
              <CheckIcon size={13} /> Telegram
            </span>
          ) : (
            <span className="chip chip--static">
              <PhoneIcon size={13} /> Solo llamada
            </span>
          )}
          {party.phone && <span>{PhoneNormalizer.format(party.phone)}</span>}
          {party.zone && <span>· {party.zone}</span>}
          {party.notes && <span>· {party.notes}</span>}
        </div>
      </div>

      <div className="door__right">
        <div className="door__timer">
          <span className={`door__timer-big${overEstimate ? ' door__timer-big--over' : ''}`}>{waited} min</span>
          <span className="muted">
            {party.estimate_min !== null ? `de ${party.estimate_min}–${party.estimate_max} est.` : 'esperando'}
          </span>
        </div>
        <div className="row">
          {party.status === 'WAITING' ? (
            <button type="button" className="btn" onClick={() => onCall(null)}>
              Avisar
            </button>
          ) : (
            <button type="button" className="btn" onClick={() => onSeat(null)}>
              Sentar
            </button>
          )}
          <button type="button" className="btn btn--outline" onClick={onLeave}>
            Baja
          </button>
        </div>
      </div>
    </article>
  );
}

function ResolvedRow({ party }: { party: WaitlistParty }) {
  const waited =
    party.seated_at !== null
      ? Math.max(0, Math.round((new Date(party.seated_at).getTime() - new Date(party.created_at).getTime()) / 60_000))
      : null;

  return (
    <article className={`door__row door__row--${party.status.toLowerCase()}`}>
      <span className="door__pos">{party.status === 'SEATED' ? '✓' : '—'}</span>
      <div className="stack stack--tight" style={{ minWidth: 0 }}>
        <div className="row">
          <strong>{party.name}</strong>
          <span className="door__size">
            <UsersIcon size={13} /> {party.party_size}
          </span>
          <span className={`status-chip status-chip--${party.status === 'SEATED' ? 'confirmed' : 'cancelled'}`}>
            {party.status === 'SEATED' ? `Sentado${party.table_label ? ` · mesa ${party.table_label}` : ''}` : 'Se fue'}
          </span>
        </div>
        <div className="meta-row">
          {waited !== null && <span>Esperó {waited} min</span>}
          {party.estimate_min !== null && (
            <span>· le dijimos {party.estimate_min}–{party.estimate_max} min</span>
          )}
        </div>
      </div>
      <span />
    </article>
  );
}
